<script lang="ts" setup>
import { ChipComponent, formatNumber, type Segment, TypographyComponent as SharedTypographyComponent } from '@metapic-apps/shared'
import { computed, ref } from 'vue'

import { useSearchSegments } from '@/api/segments.api'
import AddIcon from '@/assets/icons/add-plus.svg?component'
import CheckIcon from '@/assets/icons/check-icon.svg?component'
import HelpOldIcon from '@/assets/icons/help-old.svg?component'
import { type CampaignSegmentTarget } from '@/types/campaign'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import LoadingComponent from '@/ui-library/atoms/LoadingComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const selectedSegments = defineModel<CampaignSegmentTarget[]>('segments', { default: [] })

const query = ref('')
const { data: segments, isLoading } = useSearchSegments(query)

const selectedIds = computed(() => selectedSegments.value.map(({ id }) => id))

const toggleSegment = ({ id, name }: Pick<Segment, 'id' | 'name'>) => {
  selectedSegments.value = selectedIds.value.includes(id)
    ? selectedSegments.value.filter((segment) => segment.id !== id)
    : [...selectedSegments.value, { id, name }]
}

const removeSegment = (id: string) => (selectedSegments.value = selectedSegments.value.filter((segment) => segment.id !== id))

const memberCountLabel = (count: number) => `${formatNumber(count, 'compact')} creator${count === 1 ? '' : 's'}`

/** Element Plus sizes the menu to its content and sets it 12px off; the list should line up right under the input. */
const popperOptions = {
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    {
      name: 'sameWidth',
      enabled: true,
      phase: 'beforeWrite' as const,
      requires: ['computeStyles'],
      fn: ({ state }: { state: { elements: { popper: HTMLElement }; rects: { reference: { width: number } } } }) => {
        state.elements.popper.style.width = `${state.rects.reference.width}px`
      },
    },
  ],
}
</script>

<template>
  <div>
    <BoxComponent :mb="16">
      <FlexComponent align="center" :gap="8">
        <TypographyComponent variant="title-large">Invite creators by segment</TypographyComponent>
        <el-tooltip placement="bottom-start" effect="light" :show-arrow="false">
          <el-icon :size="16">
            <HelpOldIcon />
          </el-icon>
          <template #content>
            <BoxComponent :w="280">
              <BoxComponent :mb="4">
                <TypographyComponent variant="title-medium" color="#000000"> Invite by segment </TypographyComponent>
              </BoxComponent>
              <TypographyComponent variant="body-medium" color="#252423">
                Invite specific creators using a pre-built segment. Create and manage your segments on the Segments page.
              </TypographyComponent>
            </BoxComponent>
          </template>
        </el-tooltip>
      </FlexComponent>
    </BoxComponent>
    <el-dropdown
      trigger="click"
      placement="bottom-start"
      class="segment-invite-dropdown"
      :hide-on-click="false"
      :show-arrow="false"
      :popper-options="popperOptions"
    >
      <el-input v-model="query" size="large" placeholder="Search segment by name" class="email-invite-input" />
      <template #dropdown>
        <div class="segment-invite-menu">
          <BoxComponent v-if="isLoading" :p="16">
            <LoadingComponent />
          </BoxComponent>
          <el-dropdown-menu v-else-if="segments.length">
            <el-dropdown-item v-for="segment in segments" :key="segment.id" class="segment-option" @click="toggleSegment(segment)">
              <FlexComponent justify="space-between" align="center" :gap="16" class="segment-option__content">
                <FlexComponent direction="column" :gap="2" class="segment-option__text">
                  <SharedTypographyComponent variant="title-medium" text-truncate :title="segment.name">
                    {{ segment.name }}
                  </SharedTypographyComponent>
                  <SharedTypographyComponent variant="label-small" class="segment-option__meta">
                    {{ memberCountLabel(segment.memberCount) }}
                  </SharedTypographyComponent>
                </FlexComponent>
                <FlexComponent
                  align="center"
                  :gap="4"
                  :class="['segment-option__pill', { 'segment-option__pill_added': selectedIds.includes(segment.id) }]"
                >
                  <el-icon :size="18">
                    <CheckIcon v-if="selectedIds.includes(segment.id)" class="segment-option__tick" />
                    <AddIcon v-else />
                  </el-icon>
                  <SharedTypographyComponent variant="label-large" :font-weight="600" color="currentColor">
                    {{ selectedIds.includes(segment.id) ? 'Added' : 'Add' }}
                  </SharedTypographyComponent>
                </FlexComponent>
              </FlexComponent>
            </el-dropdown-item>
          </el-dropdown-menu>
          <BoxComponent v-else :p="16">
            <TypographyComponent is="p" variant="body-medium" text-align="center">No matching segments</TypographyComponent>
          </BoxComponent>
        </div>
      </template>
    </el-dropdown>
    <FlexComponent v-if="selectedSegments.length" class="selected-tags" :gap="8" wrap="wrap">
      <ChipComponent v-for="segment in selectedSegments" :key="segment.id" closable @close="removeSegment(segment.id)">
        {{ segment.name }}
      </ChipComponent>
    </FlexComponent>
  </div>
</template>

<style lang="scss" scoped>
.segment-invite-dropdown {
  display: block;
  width: 100%;

  :deep(.el-tooltip__trigger) {
    width: 100%;
  }
}

.segment-invite-menu {
  border-radius: 16px;
  overflow: hidden;

  :deep(.el-dropdown-menu) {
    padding: 0;
  }

  :deep(.el-dropdown-menu__item) {
    padding: 16px;
    white-space: normal;
  }
}

.segment-option__content {
  width: 100%;
}

.segment-option__text {
  min-width: 0;
}

.segment-option__meta {
  opacity: 0.48;
}

.segment-option__pill {
  flex-shrink: 0;
  height: 32px;
  color: var(--primary);

  &_added {
    color: var(--success);
  }

  :deep(.el-icon) {
    margin-right: 0;

    path {
      fill: currentColor;
    }
  }
}

// The tick artwork has no padding, unlike the 18px plus; drawn at 12px it matches the shared creator selector's tick.
.segment-option__tick {
  width: 12px;
  height: 12px;
}

.selected-tags {
  position: relative;
  top: -6px;

  max-height: 390px;
  padding: 14px 8px 8px;
  overflow: auto;

  border-bottom: 1px solid rgba(3, 30, 81, 0.12);
  border-right: 1px solid rgba(3, 30, 81, 0.12);
  border-left: 1px solid rgba(3, 30, 81, 0.12);
  border-radius: 0 0 8px 8px;
}
</style>
